import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WaterUsageCard from '../components/dashboard/WaterUsageCard';
import WaterChart from '../components/dashboard/WaterChart';
import InsightsCard from '../components/dashboard/InsightsCard';
import LeaderboardCard from '../components/dashboard/LeaderboardCard';
import ChatAssistant from '../components/ui/ChatAssistant';
import { useLanguage } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';
import { useAuth } from '@/contexts/AuthContext';
import { useMode } from '@/contexts/ModeContext';
import { Loader2, Droplets, Sprout } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

// Simulated API calls - replace with actual API calls
const fetchWaterUsageData = async (mode: 'household' | 'agriculture') => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  if (mode === 'household') {
    return {
      daily: { value: 52, unit: 'gal', percentage: 65, trend: 'down' as const, trendValue: 12 },
      monthly: { value: 1550, unit: 'gal', percentage: 40, trend: 'down' as const, trendValue: 8 },
      efficiency: { value: 86, unit: 'pts', percentage: 86, trend: 'up' as const, trendValue: 3 }
    };
  } else {
    return {
      daily: { value: 2100, unit: 'gal', percentage: 70, trend: 'down' as const, trendValue: 15 },
      monthly: { value: 21000, unit: 'gal', percentage: 45, trend: 'down' as const, trendValue: 10 },
      efficiency: { value: 92, unit: 'pts', percentage: 92, trend: 'up' as const, trendValue: 5 }
    };
  }
};

const fetchChartData = async (mode: 'household' | 'agriculture') => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return [
    { name: 'Mon', usage: mode === 'household' ? 120 : 1200, target: mode === 'household' ? 150 : 1500 },
    { name: 'Tue', usage: mode === 'household' ? 135 : 1350, target: mode === 'household' ? 150 : 1500 },
    { name: 'Wed', usage: mode === 'household' ? 180 : 1800, target: mode === 'household' ? 150 : 1500 },
    { name: 'Thu', usage: mode === 'household' ? 110 : 1100, target: mode === 'household' ? 150 : 1500 },
    { name: 'Fri', usage: mode === 'household' ? 90 : 900, target: mode === 'household' ? 150 : 1500 },
    { name: 'Sat', usage: mode === 'household' ? 75 : 750, target: mode === 'household' ? 150 : 1500 },
    { name: 'Sun', usage: mode === 'household' ? 140 : 1400, target: mode === 'household' ? 150 : 1500 }
  ];
};

const Index = () => {
  const { isRTL } = useLanguage();
  const { translate } = useTranslation();
  const { isAuthenticated, loading } = useAuth();
  const { mode } = useMode();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  const { data: usageData, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['waterUsage', mode],
    queryFn: () => fetchWaterUsageData(mode),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: chartData, isLoading: isLoadingChart } = useQuery({
    queryKey: ['chartData', mode],
    queryFn: () => fetchChartData(mode),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (loading || isLoadingUsage || isLoadingChart) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="p-4 md:p-6 relative">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-2 mb-2">
          {mode === 'household' ? (
            <Droplets className="h-6 w-6 text-primary" />
          ) : (
            <Sprout className="h-6 w-6 text-primary" />
          )}
          <h1 className="text-xl md:text-2xl font-bold" dir={isRTL ? 'rtl' : 'ltr'}>
            {translate(mode === 'household' ? "dashboard.household.title" : "dashboard.agriculture.title")}
          </h1>
        </div>
        <p className="text-muted-foreground" dir={isRTL ? 'rtl' : 'ltr'}>
          {translate(
            mode === 'household' 
              ? "dashboard.household.description"
              : "dashboard.agriculture.description"
          )}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
        <WaterUsageCard 
          title={translate(mode === 'household' ? "dashboard.household.dailyUsage" : "dashboard.agriculture.dailyIrrigation")} 
          value={usageData?.daily.value || 0}
          unit={usageData?.daily.unit || 'gal'}
          percentage={usageData?.daily.percentage || 0}
          trend={usageData?.daily.trend || 'neutral'}
          trendValue={usageData?.daily.trendValue || 0}
        />
        <WaterUsageCard 
          title={translate(mode === 'household' ? "dashboard.household.monthlyUsage" : "dashboard.agriculture.monthlyIrrigation")} 
          value={usageData?.monthly.value || 0}
          unit={usageData?.monthly.unit || 'gal'}
          percentage={usageData?.monthly.percentage || 0}
          trend={usageData?.monthly.trend || 'neutral'}
          trendValue={usageData?.monthly.trendValue || 0}
        />
        <WaterUsageCard 
          title={translate(mode === 'household' ? "dashboard.household.efficiencyScore" : "dashboard.agriculture.irrigationEfficiency")} 
          value={usageData?.efficiency.value || 0}
          unit={usageData?.efficiency.unit || 'pts'}
          percentage={usageData?.efficiency.percentage || 0}
          trend={usageData?.efficiency.trend || 'neutral'}
          trendValue={usageData?.efficiency.trendValue || 0}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <WaterChart 
          data={chartData || []}
          title={translate(mode === 'household' ? "dashboard.household.usageTrends" : "dashboard.agriculture.irrigationTrends")}
        />
        <div className="grid grid-cols-1 gap-4 md:gap-6">
          <InsightsCard mode={mode} />
          <LeaderboardCard mode={mode} />
        </div>
      </div>
      
      <ChatAssistant />
    </div>
  );
};

export default Index;
